import { EmbedHtmlBlockComponent } from '@blocksuite/blocks';
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import type { PiqueQuizEmbedModel } from './quiz-embed-block';

@customElement('pique-quiz-embed-block')
export class PiqueQuizEmbedBlockComponent extends EmbedHtmlBlockComponent {
  static override styles = css`
    @keyframes float {
      0%,
      100% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-10px);
      }
    }

    @keyframes fall {
      0% {
        transform: translateY(0) rotate(0deg);
        opacity: 0.8;
      }
      100% {
        transform: translateY(1000px) rotate(360deg);
        opacity: 0;
      }
    }

    @keyframes shine {
      0% {
        transform: translateX(-100%);
      }
      100% {
        transform: translateX(100%);
      }
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes starPop {
      0% {
        transform: scale(0) rotate(-45deg);
        opacity: 0;
      }
      50% {
        transform: scale(1.2) rotate(0deg);
        opacity: 1;
      }
      100% {
        transform: scale(1) rotate(0deg);
        opacity: 1;
      }
    }

    @keyframes progressGlow {
      0% {
        background-position: 0% 50%;
        filter: brightness(1);
      }
      50% {
        background-position: 100% 50%;
        filter: brightness(1.2);
      }
      100% {
        background-position: 0% 50%;
        filter: brightness(1);
      }
    }

    @keyframes progressFill {
      from {
        transform: scaleX(0);
      }
      to {
        transform: scaleX(1);
      }
    }

    @keyframes sparkleFloat {
      0% {
        transform: translate(-50%, 0) scale(0) rotate(-45deg);
        opacity: 0;
      }
      50% {
        transform: translate(-50%, -20px) scale(1.2) rotate(0deg);
        opacity: 1;
      }
      100% {
        transform: translate(-50%, -40px) scale(1) rotate(45deg);
        opacity: 0;
      }
    }

    @keyframes celebrate {
      0% {
        transform: translateY(0);
      }
      50% {
        transform: translateY(-4px);
      }
      100% {
        transform: translateY(0);
      }
    }

    :host {
      width: 100%;
      max-width: var(--affine-editor-width);
      margin: 0 auto;
      padding: 0 var(--affine-editor-side-padding);
      display: block;
    }

    .quiz-container {
      background: var(--affine-brand-color);
      padding: 20px;
      border-radius: 16px;
      border: 2px solid #333;
      box-shadow: 4px 4px 0px 0px #333;
      overflow: visible;
      transition: all 0.3s ease;
      animation: fadeIn 0.5s ease-out forwards;
      max-width: 24rem;
      margin: 0 auto;
      transform: translateY(0);
      position: relative;
      z-index: 1000;
      pointer-events: auto !important;
    }

    .quiz-container:hover {
      transform: translateY(-2px);
    }

    .quiz-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
    }

    .quiz-progress,
    .quiz-score {
      background: white;
      color: #333;
      padding: 4px 8px;
      border-radius: 8px;
      border: 2px solid #333;
      box-shadow: 2px 2px 0px 0px #333;
      font-family: monospace;
      font-weight: bold;
      font-size: 0.85em;
    }

    .question {
      font-size: 16px;
      font-weight: bold;
      color: #333;
      margin-bottom: 16px;
    }

    .options {
      position: relative;
      z-index: 1001;
      pointer-events: auto !important;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .option {
      width: 100%;
      padding: 10px 12px;
      border-radius: 12px;
      border: 2px solid #333;
      background: white;
      color: #333;
      cursor: pointer;
      font-weight: 500;
      font-size: 0.9em;
      position: relative;
      overflow: hidden;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: space-between;
      box-shadow: 2px 2px 0px 0px #333;
      text-align: left;
    }

    .option::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        120deg,
        transparent 0%,
        transparent 50%,
        rgba(255, 255, 255, 0.2) 50%,
        transparent 100%
      );
      background-size: 200% 100%;
      transition: background-position 0.3s ease;
    }

    .option:hover::after {
      background-position: -50% 0;
    }

    .option.correct {
      background: linear-gradient(120deg, #86efac, #4ade80);
      color: #333;
      animation: celebrate 0.5s ease-out;
    }

    .option.incorrect {
      background: linear-gradient(120deg, #fca5a5, #f87171);
      color: #333;
    }

    .option.disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
    }

    .explanation {
      margin-top: 12px;
      margin-bottom: 12px;
      padding: 10px;
      background: white;
      color: #333;
      border-radius: 10px;
      border: 2px solid #333;
      box-shadow: 2px 2px 0px 0px #333;
      font-size: 0.85em;
    }

    .quiz-navigation {
      display: flex;
      justify-content: space-between;
      gap: 8px;
    }

    .nav-button {
      background: white;
      color: #333;
      padding: 6px 12px;
      border-radius: 8px;
      border: 2px solid #333;
      box-shadow: 2px 2px 0px 0px #333;
      cursor: pointer;
      font-weight: bold;
      font-size: 0.85em;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      transition: all 0.2s ease;
      min-width: 80px;
      justify-content: center;
      position: relative;
      text-decoration: none;
      outline: none;
    }

    .nav-button:hover:not(:disabled) {
      transform: translate(2px, 2px);
      box-shadow: 2px 2px 0px 0px #333;
      background: #f0f0f0;
    }

    .nav-button:active:not(:disabled) {
      transform: translate(4px, 4px);
      box-shadow: none;
    }

    .nav-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }

    .option-icon {
      width: 16px;
      height: 16px;
    }

    .option-icon.correct {
      color: #16a34a;
    }

    .option-icon.incorrect {
      color: #dc2626;
    }

    .sparkles-icon {
      width: 48px;
      height: 48px;
      color: #333;
      animation: float 6s ease-in-out infinite;
    }

    .quiz-results {
      text-align: center;
      animation: fadeIn 0.5s ease-out forwards;
      color: #333;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      padding: 1.5rem;
    }

    .score {
      font-size: 48px;
      font-weight: bold;
      margin: 24px 0;
      animation: float 6s ease-in-out infinite;
      background: linear-gradient(120deg, #4ade80, #86efac);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      display: inline-block;
      padding: 0.5rem;
      position: relative;
      text-shadow: none;
    }

    .score-message {
      font-size: 18px;
      font-weight: bold;
      color: #333;
      margin: 0;
      padding: 0;
    }

    .quiz-click-mask {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 999;
      pointer-events: auto !important;
    }

    .quiz-content {
      position: relative;
      z-index: 1000;
      pointer-events: auto !important;
    }

    .quiz-progress-wrapper {
      margin: 24px -24px;
      padding: 0 24px;
      position: relative;
    }

    .quiz-progress-container {
      width: 100%;
      height: 6px;
      background: rgba(255, 255, 255, 0.3);
      border-radius: 10px;
      margin: 16px 0;
      position: relative;
      overflow: visible;
      border: 1px solid #333;
      box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
      padding: 0 2px;
    }

    .quiz-progress-bar {
      height: 100%;
      border-radius: 10px;
      background: linear-gradient(90deg, #60a5fa, #c084fc, #f472b6, #60a5fa);
      background-size: 300% 100%;
      animation: progressGlow 5s ease infinite;
      transform-origin: left;
      transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .quiz-progress-marker {
      position: absolute;
      width: 16px;
      height: 16px;
      background: white;
      border: 1.5px solid #333;
      border-radius: 50%;
      top: -5px;
      transform: translateX(-50%);
      transition: left 0.5s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      z-index: 1;
    }

    .quiz-progress-marker.milestone {
      background: #60a5fa;
      border-color: #333;
    }

    .quiz-score {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      transition: transform 0.3s ease;
    }

    .quiz-score.updated {
      animation: celebrate 0.5s ease-out;
    }

    .score-star {
      display: inline-block;
      margin-left: 4px;
      animation: starPop 0.5s ease-out forwards;
    }

    .quiz-stats {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
    }

    .sparkle {
      position: absolute;
      font-size: 14px;
      pointer-events: none;
      animation: sparkleFloat 1s ease-out forwards;
    }
  `;

  override get model(): PiqueQuizEmbedModel {
    return super.model as PiqueQuizEmbedModel;
  }

  @state()
  protected accessor isSelected = false;

  private lastScore: number | undefined;

  private createSparkle(x: number, y: number) {
    const sparkle = document.createElement('div');
    sparkle.className = 'sparkle';
    sparkle.textContent = '✨';
    sparkle.style.left = `${x}px`;
    sparkle.style.top = `${y}px`;
    document.body.append(sparkle);
    setTimeout(() => sparkle.remove(), 1000);
  }

  private handleOptionClick(questionId: string, answer: string) {
    if (this.model.isComplete) return;

    const currentQuestion = this.model.getCurrentQuestion();
    if (!currentQuestion) return;

    this.model.answerQuestion(questionId, answer);

    // Celebrate correct answer
    if (answer === currentQuestion.answer) {
      const button = this.shadowRoot?.querySelector('.option.correct');
      if (button) {
        const rect = button.getBoundingClientRect();
        // Create multiple sparkles
        for (let i = 0; i < 3; i++) {
          setTimeout(() => {
            this.createSparkle(
              rect.left + rect.width * Math.random(),
              rect.top + rect.height * Math.random()
            );
          }, i * 150);
        }
      }
    }

    // Animate score if it changed
    if (this.model.score !== this.lastScore) {
      const scoreElement = this.shadowRoot?.querySelector('.quiz-score');
      if (scoreElement) {
        scoreElement.classList.remove('updated');
        // Force reflow by reading a layout property
        void scoreElement.getBoundingClientRect();
        scoreElement.classList.add('updated');
      }
      this.lastScore = this.model.score;
    }
  }

  private handleNextClick() {
    this.model.nextQuestion();
  }

  private handlePreviousClick() {
    this.model.previousQuestion();
  }

  private handleRestartClick() {
    this.model.resetQuiz();
  }

  private renderOptionIcon(isCorrect: boolean) {
    if (isCorrect) {
      return html`
        <svg class="option-icon correct" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
          />
        </svg>
      `;
    }
    return html`
      <svg class="option-icon incorrect" viewBox="0 0 24 24">
        <path
          fill="currentColor"
          d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"
        />
      </svg>
    `;
  }

  private renderSparklesIcon() {
    return html`
      <svg class="sparkles-icon" viewBox="0 0 24 24">
        <path
          fill="currentColor"
          d="M12,1L9,9L1,12L9,15L12,23L15,15L23,12L15,9L12,1Z M2,12L7,10L9,5L11,10L16,12L11,14L9,19L7,14L2,12Z"
        />
      </svg>
    `;
  }

  private getScoreMessage(score: number): string {
    if (score >= 90) return "Outstanding! You're a master! 🎉";
    if (score >= 80) return 'Great job! Almost perfect! ⭐';
    if (score >= 70) return 'Good work! Keep it up! 🌟';
    if (score >= 60) return 'Not bad! Room for improvement! 💪';
    return "Keep practicing! You'll get there! 🚀";
  }

  private renderScoreWithStar(score: number | undefined) {
    return html`
      <div class="quiz-score">
        Score: ${score !== undefined ? Math.round(score) : 0}
      </div>
    `;
  }

  override renderBlock() {
    const quiz = this.model.getQuiz();
    if (!quiz) {
      return html`<div class="quiz-container">No quiz data available</div>`;
    }

    const currentQuestion = this.model.getCurrentQuestion();
    if (!currentQuestion) {
      return html`<div class="quiz-container">No questions available</div>`;
    }

    return this.renderEmbed(
      () => html`
        <div @click=${(e: MouseEvent) => e.stopPropagation()}>
          <div
            class=${classMap({
              'quiz-container': true,
              selected: this.isSelected,
            })}
          >
            <div
              class="quiz-click-mask"
              @click=${(e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            ></div>

            <div class="quiz-content">
              <div class="quiz-header">
                <div class="quiz-progress">
                  ${this.model.currentQuestionIndex + 1}/${quiz.questions
                    .length}
                </div>
                ${this.renderScoreWithStar(this.model.score)}
              </div>

              ${this.model.isComplete
                ? this.renderResults()
                : html`
                    <div class="question">${currentQuestion.question}</div>
                    <div class="options">
                      ${currentQuestion.options?.map(
                        (option, _index) => html`
                          <button
                            class=${classMap({
                              option: true,
                              selected:
                                this.model.userAnswers[currentQuestion.id] ===
                                option,
                              correct:
                                this.model.userAnswers[currentQuestion.id] ===
                                  option && option === currentQuestion.answer,
                              incorrect:
                                this.model.userAnswers[currentQuestion.id] ===
                                  option && option !== currentQuestion.answer,
                              disabled:
                                this.model.userAnswers[currentQuestion.id] !==
                                undefined,
                            })}
                            @click=${(e: MouseEvent) => {
                              e.stopPropagation();
                              this.handleOptionClick(
                                currentQuestion.id,
                                option
                              );
                            }}
                            ?disabled=${this.model.userAnswers[
                              currentQuestion.id
                            ] !== undefined}
                          >
                            <span>${option}</span>
                            ${this.model.userAnswers[currentQuestion.id] ===
                            option
                              ? this.renderOptionIcon(
                                  option === currentQuestion.answer
                                )
                              : ''}
                          </button>
                        `
                      )}
                    </div>
                    ${this.model.userAnswers[currentQuestion.id]
                      ? html`
                          <div class="explanation">
                            <strong>Fun Fact:</strong>
                            ${currentQuestion.explanation}
                          </div>
                        `
                      : ''}
                    <div class="quiz-progress-wrapper">
                      <div class="quiz-progress-container">
                        <div
                          class="quiz-progress-bar"
                          style="transform: scaleX(${(this.model
                            .currentQuestionIndex +
                            1) /
                          quiz.questions.length})"
                        ></div>
                        <div
                          class=${classMap({
                            'quiz-progress-marker': true,
                            milestone:
                              (this.model.currentQuestionIndex + 1) % 3 === 0,
                          })}
                          style="left: ${((this.model.currentQuestionIndex +
                            1) /
                            quiz.questions.length) *
                          100}%"
                        ></div>
                      </div>
                    </div>
                    <div class="quiz-navigation">
                      <button
                        class="nav-button"
                        ?disabled=${this.model.currentQuestionIndex === 0}
                        @click=${(e: MouseEvent) => {
                          e.stopPropagation();
                          this.handlePreviousClick();
                        }}
                      >
                        <svg viewBox="0 0 24 24">
                          <path
                            fill="currentColor"
                            d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12l4.58-4.59z"
                          />
                        </svg>
                        Previous
                      </button>
                      <button
                        class="nav-button"
                        ?disabled=${!this.model.userAnswers[currentQuestion.id]}
                        @click=${(e: MouseEvent) => {
                          e.stopPropagation();
                          this.handleNextClick();
                        }}
                      >
                        ${this.model.currentQuestionIndex ===
                        quiz.questions.length - 1
                          ? 'Finish'
                          : html`
                              Next
                              <svg viewBox="0 0 24 24">
                                <path
                                  fill="currentColor"
                                  d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6-6-6z"
                                />
                              </svg>
                            `}
                      </button>
                    </div>
                  `}
            </div>
          </div>
        </div>
      `
    );
  }

  private renderResults() {
    const quiz = this.model.getQuiz();
    if (!quiz || this.model.score === undefined) return null;

    const score = Math.round(this.model.score);

    return html`
      <div class="quiz-results">
        ${this.renderSparklesIcon()}
        <h2 class="score-message">You did it! 🎉</h2>
        <div class="score">${score}<span style="font-size: 0.7em">%</span></div>
        <div class="score-message">${this.getScoreMessage(score)}</div>
        <button class="nav-button" @click=${this.handleRestartClick}>
          <svg viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"
            />
          </svg>
          Play Again!
        </button>
      </div>
    `;
  }
}
